import { Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AssistantService, MessageAssistant } from 'src/app/core/services/entities/assistant.service';

/**
 * Assistant IA (ouvert depuis le bouton « + ») : questions en langage courant sur la ferme.
 * Les chiffres viennent du serveur ; quota quotidien affiché ; message clair si indisponible.
 */
@Component({
  selector: 'bay-assistant',
  template: `
    <div class="bay-assist">
      <div *ngIf="indisponible" class="bay-assist-info" data-etat="indisponible">{{ indisponible }}</div>
      <div class="bay-assist-fil" #fil>
        <div *ngIf="!messages.length && !indisponible" class="bay-assist-accueil" data-etat="accueil">
          Posez une question sur votre ferme, par exemple :
          <button type="button" *ngFor="let s of exemples" class="bay-assist-sugg" (click)="envoyer(s)">{{ s }}</button>
        </div>
        <div *ngFor="let m of messages" class="bay-assist-msg" [class.bay-assist-moi]="m.role === 'utilisateur'">{{ m.texte }}</div>
        <div *ngIf="envoi" class="bay-assist-msg bay-assist-attente" data-etat="attente">L'assistant réfléchit…</div>
        <div *ngIf="suggestions.length && !envoi" class="bay-assist-suggs">
          <button type="button" *ngFor="let s of suggestions" class="bay-assist-sugg" (click)="envoyer(s)">{{ s }}</button>
        </div>
        <div *ngIf="erreur" class="bay-assist-erreur" role="alert" data-etat="erreur">{{ erreur }}</div>
      </div>
      <form class="bay-assist-saisie" (ngSubmit)="envoyer(question)">
        <input class="bay-input" name="question" [(ngModel)]="question" [disabled]="envoi || !!indisponible"
               placeholder="Ex. : combien me doivent mes clients ?" autocomplete="off" maxlength="500">
        <button type="submit" class="bay-btn bay-btn-primary bay-btn-sm" [disabled]="envoi || !question.trim() || !!indisponible">Envoyer</button>
      </form>
      <div class="bay-assist-pied">
        <span *ngIf="restantes !== null">{{ restantes }} question(s) restante(s) aujourd'hui · </span>L'assistant s'appuie sur vos données ; vérifiez avant de décider.
      </div>
    </div>
  `,
  styles: [`
    .bay-assist { display: grid; gap: 10px; }
    .bay-assist-fil { max-height: 50vh; overflow-y: auto; display: grid; gap: 8px; padding: 2px; }
    .bay-assist-accueil { color: #707a6c; font-size: 14px; display: grid; gap: 6px; }
    .bay-assist-msg { background: #f2f5ee; border-radius: 14px; padding: 10px 12px; white-space: pre-line; font-size: 14px; color: #181d17; max-width: 92%; }
    .bay-assist-moi { background: #0d631b; color: #fff; justify-self: end; }
    .bay-assist-attente { color: #707a6c; font-style: italic; }
    .bay-assist-suggs { display: flex; flex-wrap: wrap; gap: 6px; }
    .bay-assist-sugg { border: 1px solid #cfd8c8; background: #fff; border-radius: 999px; padding: 5px 12px; font-size: 13px; text-align: left; }
    .bay-assist-erreur, .bay-assist-info { background: #fff6e0; color: #8a5a00; border-radius: 12px; padding: 10px 12px; font-size: 13.5px; }
    .bay-assist-saisie { display: flex; gap: 8px; }
    .bay-assist-pied { color: #707a6c; font-size: 12px; }
  `]
})
export class AssistantComponent implements OnChanges {
  /** Le panneau s'initialise à chaque ouverture */
  @Input() ouvert = false;
  @ViewChild('fil') fil?: ElementRef<HTMLElement>;

  readonly exemples = ['Combien me doivent mes clients ?', 'Quelles ont été mes plus grosses dépenses ce mois-ci ?', 'Comment va le poulailler ?'];
  messages: MessageAssistant[] = [];
  suggestions: string[] = [];
  question = '';
  envoi = false;
  erreur: string | null = null;
  indisponible: string | null = null;
  restantes: number | null = null;
  private statutCharge = false;

  constructor(private service: AssistantService) {}

  ngOnChanges(): void {
    if (this.ouvert && !this.statutCharge) {
      this.statutCharge = true;
      this.service.statut().subscribe({
        next: s => {
          this.restantes = s.questionsRestantes;
          this.indisponible = !s.actif ? 'L\'assistant IA n\'est pas activé pour le moment.'
            : s.questionsRestantes <= 0 ? 'Vous avez posé toutes les questions du jour. L\'assistant répondra de nouveau demain.' : null;
        },
        error: () => { this.indisponible = 'L\'assistant est réservé à l\'administrateur et au comptable de la ferme.'; },
      });
    }
  }

  envoyer(texte: string): void {
    const q = (texte || '').trim();
    if (!q || this.envoi || this.indisponible) { return; }
    const historique = this.messages.slice(-8);
    this.messages = [...this.messages, { role: 'utilisateur', texte: q }];
    this.question = '';
    this.suggestions = [];
    this.erreur = null;
    this.envoi = true;
    this.defiler();
    this.service.question(q, historique).subscribe({
      next: r => {
        this.envoi = false;
        this.messages = [...this.messages, { role: 'assistant', texte: r.reponse }];
        this.suggestions = r.suggestions || [];
        this.restantes = r.questionsRestantes;
        this.defiler();
      },
      error: (e: HttpErrorResponse) => {
        this.envoi = false;
        this.erreur = e?.error?.message || 'L\'assistant ne répond pas pour le moment. Réessayez plus tard.';
        if (e?.status === 429) { this.restantes = 0; }
      },
    });
  }

  private defiler(): void {
    setTimeout(() => { const f = this.fil?.nativeElement; if (f) { f.scrollTop = f.scrollHeight; } });
  }
}
